import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Volume2, VolumeX, ChevronLeft, ChevronRight, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { PRODUCT_TYPES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
export const speechSupported = () => !!SR;

const ZONES = ["North", "South", "West", "Exports"];
const firstNumber = (t) => {
  const m = String(t).replace(/,/g, "").match(/\d+(\.\d+)?/);
  return m ? m[0] : "";
};
const matchOption = (t, options) => {
  const low = String(t).toLowerCase();
  return options.find((o) => low.includes(o.toLowerCase().split(" ")[0].toLowerCase())) || "";
};
const parseSpokenDate = (t) => {
  const d = new Date(t);
  if (!isNaN(d.getTime()) && d.getFullYear() > 2000) return d.toISOString().slice(0, 10);
  return "";
};

const LEAD_STEPS = [
  { key: "company", prompt: "Say the company name", type: "text" },
  { key: "project_name", prompt: "Say the project name", type: "text" },
  { key: "zone", prompt: "Which zone? North, South, West or Exports", type: "option", options: ZONES },
  { key: "branch", prompt: "Say the branch or city", type: "text" },
  { key: "univic_specified", prompt: "Is UniVicoustic specified in this project? Yes or No", type: "option", options: ["Yes", "No"] },
  { key: "value_lakhs", prompt: "Say the approximate value in Crores", type: "number" },
  { key: "edc_date", prompt: "Say the expected closure date (e.g. 30 September 2026)", type: "date" },
  { key: "product_type", prompt: "Which product type? HD, VMT, Both, or Ceiling Products", type: "option", options: PRODUCT_TYPES },
  { key: "end_client_name", prompt: "Say the end client decision maker's name", type: "text" },
  { key: "end_client_phone", prompt: "Say the contact number, digit by digit", type: "phone" },
  { key: "remarks", prompt: "Any remarks or project details?", type: "long" },
];
const DEAL_STEPS = [
  { key: "name", prompt: "Say the opportunity or project name", type: "text" },
  { key: "company", prompt: "Say the company name", type: "text" },
  { key: "zone", prompt: "Which zone? North, South, West or Exports", type: "option", options: ZONES },
  { key: "branch", prompt: "Say the branch or city", type: "text" },
  { key: "univic_specified", prompt: "Is UniVicoustic specified in this project? Yes or No", type: "option", options: ["Yes", "No"] },
  { key: "value_lakhs", prompt: "Say the deal value in Crores", type: "number" },
  { key: "close_date", prompt: "Say the expected close date (e.g. 30 September 2026)", type: "date" },
  { key: "product_type", prompt: "Which product type? HD, VMT, Both, or Ceiling Products", type: "option", options: PRODUCT_TYPES },
];

export function VoiceGuidedIntake({ mode = "lead", onExtracted }) {
  const steps = mode === "lead" ? LEAD_STEPS : DEAL_STEPS;
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [text, setText] = useState("");
  const [dateVal, setDateVal] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const rec = useRef(null);
  const finalRef = useRef("");
  const voiceOnRef = useRef(true);
  const step = steps[idx];

  const speak = (msg) => {
    try {
      if (!voiceOnRef.current || !window.speechSynthesis) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(msg);
      u.rate = 1.05;
      window.speechSynthesis.speak(u);
    } catch (err) { console.warn("Speech synthesis unavailable:", err); }
  };

  const stopListening = () => {
    try { rec.current?.stop(); } catch (err) { console.warn("Speech recognition stop failed:", err); }
    setListening(false);
  };

  const startListening = () => {
    if (!SR) return;
    stopListening();
    const r = new SR();
    r.lang = "en-IN";
    r.interimResults = true;
    r.continuous = true;
    finalRef.current = text ? `${text} ` : "";
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalRef.current += t + " ";
        else interim += t;
      }
      setText((finalRef.current + interim).trimStart());
    };
    r.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed") toast.error("Microphone permission denied");
    };
    r.onend = () => setListening(false);
    rec.current = r;
    try { r.start(); setListening(true); } catch (err) { console.warn("Speech recognition start failed:", err); }
  };

  const beginStep = (i, existing = {}) => {
    const s = steps[i];
    const prev = existing[s.key];
    setText(prev || "");
    setDateVal(s.type === "date" ? (prev || "") : "");
    finalRef.current = prev ? `${prev} ` : "";
    speak(s.prompt);
    setTimeout(startListening, 300);
  };

  const openWizard = () => {
    setAnswers({});
    setIdx(0);
    setOpen(true);
    setTimeout(() => beginStep(0, {}), 400);
  };

  const closeWizard = (v) => {
    if (!v) {
      stopListening();
      try { window.speechSynthesis?.cancel(); } catch (err) { console.warn("Speech cancel failed:", err); }
    }
    setOpen(v);
  };

  const valueForStep = () => {
    const raw = text.trim();
    if (step.type === "date") return dateVal || parseSpokenDate(raw);
    if (step.type === "number") return firstNumber(raw);
    if (step.type === "phone") return raw.replace(/[^\d+]/g, "");
    if (step.type === "option") return matchOption(raw, step.options) || raw;
    return raw;
  };

  const goto = (nextIdx, savedVal) => {
    stopListening();
    const next = { ...answers, [step.key]: savedVal };
    setAnswers(next);
    if (nextIdx >= steps.length) {
      finish(next);
      return;
    }
    setIdx(nextIdx);
    beginStep(nextIdx, next);
  };

  const finish = (a) => {
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    setOpen(false);
    const out = {};
    if (mode === "lead") {
      if (a.company) out.company = a.company;
      if (a.project_name) out.project_name = a.project_name;
      if (a.zone) out.zone = a.zone;
      if (a.branch) out.branch = a.branch;
      if (a.univic_specified) out.univic_specified = a.univic_specified;
      if (a.value_lakhs) out.expected_value = Number(a.value_lakhs) * 10000000;
      if (a.edc_date) out.edc_date = a.edc_date;
      if (a.product_type) out.product_type = a.product_type;
      if (a.end_client_name) out.end_client_name = a.end_client_name;
      if (a.end_client_phone) out.end_client_phone = a.end_client_phone;
      if (a.remarks) out.remarks = a.remarks;
    } else {
      if (a.name) out.name = a.name;
      if (a.company) out.company = a.company;
      if (a.zone) out.zone = a.zone;
      if (a.branch) out.branch = a.branch;
      if (a.univic_specified) out.univic_specified = a.univic_specified;
      if (a.value_lakhs) out.deal_value_lakhs = a.value_lakhs;
      if (a.close_date) out.expected_close_date = a.close_date;
      if (a.product_type) out.product_type = a.product_type;
    }
    onExtracted(out);
    toast.success("Voice capture done — review the form and save");
  };

  useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);
  useEffect(() => () => { stopListening(); }, []);

  if (!SR) return null;

  return (
    <>
      <Button variant="outline" onClick={openWizard} className="h-9 gap-1.5 border-brand/40 text-brand hover:bg-brand/5 hover:text-brand" data-testid={`voice-guided-btn-${mode}`}>
        <Mic className="h-4 w-4" /> Add by Voice
      </Button>
      <Dialog open={open} onOpenChange={closeWizard}>
        <DialogContent className="sm:max-w-lg" data-testid="voice-guided-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-brand" /> Guided voice {mode === "lead" ? "lead" : "opportunity"} capture
              <button type="button" onClick={() => setVoiceOn((v) => !v)} className="ml-auto text-slate-400 hover:text-slate-600" title={voiceOn ? "Mute prompts" : "Speak prompts aloud"} data-testid="voice-prompt-toggle">
                {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </button>
            </DialogTitle>
            <DialogDescription>Step {idx + 1} of {steps.length} — speak, watch the live transcript, edit if needed, then Next.</DialogDescription>
          </DialogHeader>

          <div className="flex gap-1 mb-1">
            {steps.map((s, i) => <div key={s.key} className={`h-1 flex-1 rounded ${i < idx ? "bg-emerald-400" : i === idx ? "bg-brand" : "bg-slate-200"}`} />)}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-800 mb-2" data-testid="voice-step-prompt">{step.prompt}</p>
            <div className="flex items-start gap-2">
              <button type="button" onClick={listening ? stopListening : startListening}
                className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center border transition-colors ${listening ? "bg-red-500 border-red-500 text-white animate-pulse" : "bg-white border-slate-200 text-slate-500 hover:text-brand"}`}
                title={listening ? "Stop listening" : "Start listening"} data-testid="voice-mic-toggle">
                {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
              <div className="flex-1 space-y-2">
                {step.type === "long"
                  ? <Textarea value={text} onChange={(e) => { setText(e.target.value); finalRef.current = e.target.value + " "; }} rows={3} placeholder={listening ? "Listening… speak now" : "Tap the mic or type here"} data-testid="voice-transcript" />
                  : <Input value={text} onChange={(e) => { setText(e.target.value); finalRef.current = e.target.value + " "; }} placeholder={listening ? "Listening… speak now" : "Tap the mic or type here"} className="h-10" data-testid="voice-transcript" />}
                {step.type === "date" && (
                  <div className="flex items-center gap-2">
                    <Input type="date" value={dateVal || parseSpokenDate(text)} onChange={(e) => setDateVal(e.target.value)} className="h-9 w-44" data-testid="voice-date-input" />
                    <span className="text-[11px] text-slate-400">confirm the date here</span>
                  </div>
                )}
                {step.type === "option" && (
                  <div className="flex flex-wrap gap-1.5">
                    {step.options.map((o) => (
                      <button key={o} type="button" onClick={() => { setText(o); finalRef.current = o + " "; }}
                        className={`text-[11px] px-2 py-1 rounded-full border ${text.trim() === o ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-brand"}`}>
                        {o}
                      </button>
                    ))}
                  </div>
                )}
                {step.type === "number" && text && <p className="text-[11px] text-slate-400">Understood: <strong>{firstNumber(text) || "—"} Cr</strong></p>}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" className="h-9 gap-1" disabled={idx === 0} onClick={() => goto(idx - 1, valueForStep())} data-testid="voice-back-btn">
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
            <button type="button" onClick={() => goto(idx + 1, "")} className="text-xs text-slate-400 hover:text-slate-600" data-testid="voice-skip-btn">Skip</button>
            <Button size="sm" className="h-9 gap-1 bg-slate-900 hover:bg-slate-800" onClick={() => goto(idx + 1, valueForStep())} data-testid="voice-next-btn">
              {idx === steps.length - 1 ? <><Check className="h-4 w-4" /> Finish</> : <>Next <ChevronRight className="h-4 w-4" /></>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
